import { useRuntimeConfig } from "#app"
import { camelToSnakeDeep, snakeToCamelDeep } from "./caseConvert"
import { clearSession, isSessionExpiring, loadSession, saveSession } from "./tokenStore"
import { retryTransient } from "../retry"
import { API_BASE } from "../apiBase"
import type { AuthResponse } from "~/composables/useAuth"

// docs.iopta.org/docs/api/introduction
const DEFAULT_API_BASE = API_BASE

// Preference order: `NUXT_PUBLIC_API_BASE` (settable per-container at `docker run` time, read at
// request time by the Nitro server — see `nuxt.config.ts`) > `VITE_API_BASE` (baked in at
// `npm run build` time, e.g. for `npm run dev`/non-container builds) > the hardcoded default.
function apiBase(): string {
  return useRuntimeConfig().public.apiBase || (import.meta.env.VITE_API_BASE as string | undefined) || DEFAULT_API_BASE
}

export interface RestErrorBody {
  code?: number
  message?: string
  details?: unknown[]
}

/** Message text deliberately includes "401"/"Unauthenticated" so the shared
 * `AUTH_ERROR_PATTERN` check in `~/lib/tauriInvoke.ts` catches it the same way it catches a
 * stringified `tonic::Status` from the Tauri build, and triggers the same redirect-to-login. */
export class RestApiError extends Error {
  readonly status: number
  readonly body?: RestErrorBody
  /** Parsed `Retry-After` response header, if any — a floor for the retry delay (see `../retry.ts`). */
  readonly retryAfterMs?: number

  constructor(status: number, body?: RestErrorBody, retryAfterMs?: number) {
    const label = status === 401 ? "Unauthenticated (401)" : `${body?.message ?? "Request failed"} (${status})`
    super(label)
    this.name = "RestApiError"
    this.status = status
    this.body = body
    this.retryAfterMs = retryAfterMs
  }
}

/** `Retry-After` is either a number of seconds or an HTTP date. */
function parseRetryAfterMs(header: string | null): number | undefined {
  if (!header) return undefined
  const seconds = Number(header)
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000)
  const date = Date.parse(header)
  return Number.isNaN(date) ? undefined : Math.max(0, date - Date.now())
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE"
  body?: unknown
  query?: Record<string, unknown>
  /** false for the anonymous-auth endpoints themselves, and the refresh call. */
  auth?: boolean
}

async function rawRequest(path: string, method: string, body: unknown, token?: string): Promise<unknown> {
  // The server rejects a POST with no body at all ("Request JSON payload is not correctly
  // formatted") rather than treating a missing body as an implicit `{}` — every route in
  // `routes.ts` that POSTs without an explicit `body` (e.g. `start_channel_connection`,
  // `leave_group`, `confirm_upload`, ...) relies on this default.
  const hasBody = body !== undefined || method === "POST"
  const res = await fetch(`${apiBase()}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: hasBody ? JSON.stringify(snakeToCamelDeep(body ?? {})) : undefined,
  })

  const text = await res.text()

  if (!res.ok) {
    // An overloaded proxy answers 429/503 with whatever body it likes (often HTML) — the status
    // must survive that instead of being masked by a JSON parse error.
    let errorBody: RestErrorBody | undefined
    try {
      errorBody = text ? (JSON.parse(text) as RestErrorBody) : undefined
    } catch {
      errorBody = undefined
    }
    throw new RestApiError(res.status, errorBody, parseRetryAfterMs(res.headers.get("Retry-After")))
  }

  return camelToSnakeDeep(text ? JSON.parse(text) : {})
}

// The backend's explicit "this refresh token is no longer good" answer. Only that (or a plain 401)
// ends the session — an overloaded or restarting service must not log the user out.
const REFRESH_REFUSED_MESSAGE = "Failed to refresh token with Keycloak"

function isRefreshRefused(error: unknown): boolean {
  if (!(error instanceof RestApiError)) return false
  if (error.status === 401) return true
  return error.status === 400 && (error.body?.message ?? "").includes(REFRESH_REFUSED_MESSAGE)
}

// After a refresh has failed (its own retries included), further attempts are short-circuited for
// this long — otherwise every command retried by `../retry.ts` would run a full refresh retry
// cycle of its own against a server that just asked for less load.
const REFRESH_COOLDOWN_MS = 10_000
let refreshCooldown: { until: number; error: unknown } | null = null
let refreshInFlight: Promise<string | null> | null = null

/** POST /v1/users/token/refresh — bypasses the normal auth/retry wrapper since it's what that
 * wrapper calls into. Persists the refreshed session on success. Resolves to null (and clears the
 * session) only when the server refused the refresh token; any other failure is thrown with the
 * session left intact. Concurrent callers share one request. */
export function refreshAccessToken(): Promise<string | null> {
  refreshInFlight ??= doRefreshAccessToken().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

async function doRefreshAccessToken(): Promise<string | null> {
  const session = loadSession()
  if (!session) return null
  if (refreshCooldown && Date.now() < refreshCooldown.until) throw refreshCooldown.error
  refreshCooldown = null
  try {
    const refreshed = (await retryTransient("refresh_token", () =>
      rawRequest("/v1/users/token/refresh", "POST", { refresh_token: session.refresh_token }),
    )) as AuthResponse
    saveSession(refreshed)
    return refreshed.access_token
  } catch (e) {
    if (isRefreshRefused(e)) {
      clearSession()
      return null
    }
    refreshCooldown = { until: Date.now() + REFRESH_COOLDOWN_MS, error: e }
    throw e
  }
}

export async function currentAccessToken(): Promise<string | null> {
  const session = loadSession()
  if (!session) return null
  if (!isSessionExpiring(session)) return session.access_token
  try {
    return await refreshAccessToken()
  } catch (e) {
    // The refresh is proactive (see `isSessionExpiring`), so the current token may still be good.
    if (Date.now() < session.expires_at) return session.access_token
    throw e
  }
}

export async function restRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, query, auth = true } = options

  let url = path
  if (query) {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(snakeToCamelDeep<Record<string, unknown>>(query))) {
      if (value === undefined || value === null) continue
      params.set(key, String(value))
    }
    const qs = params.toString()
    if (qs) url += `?${qs}`
  }

  const token = auth ? await currentAccessToken() : undefined
  if (auth && !token) throw new RestApiError(401)

  try {
    return (await rawRequest(url, method, body, token ?? undefined)) as T
  } catch (e) {
    // One retry after a forced refresh — `currentAccessToken()` only refreshes proactively near
    // expiry, this catches the case where the server invalidated the token for another reason.
    if (auth && e instanceof RestApiError && e.status === 401) {
      const refreshed = await refreshAccessToken()
      if (refreshed) return (await rawRequest(url, method, body, refreshed)) as T
    }
    throw e
  }
}
